// @flow

import React, { useState, useCallback } from 'react';
import i18n from 'app/common/lib/i18n';
import IconButton from 'app/common/react/components/common/ExperimentalLegoGestalt/IconButton';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import Text from 'app/common/react/components/common/ExperimentalLegoGestalt/Text';
import { Box, Icon } from 'gestalt';

const styles = {
  container: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '18px',
    height: '32px',
  },
  containerHovered: {
    backgroundColor: 'rgba(255, 255, 255, 1.0)',
  },
  text: {
    color: '#333333',
  },
};

const ICON_WIDTH_THRESHOLD_PX = 72;
const ICON_SIZE_THRESHOLD_PX = 58;

export type SourceLinkStyle = 'default' | 'icon-only';

type Props = {|
  pinId: string,
  sourceUrl: string,
  domain: string,
  onClick: () => void,
  sourceLinkStyle: SourceLinkStyle,
  maxWidth: number,
|};

// SourceLink is the element that allows the user to click through directly to the pin's source
export default function ProductSourceLink({
  pinId,
  sourceUrl,
  domain,
  onClick,
  sourceLinkStyle,
  maxWidth,
}: Props) {
  const [isHovered, setHoverState] = useState(false);
  const handleMouseEnter = useCallback(() => setHoverState(true), [setHoverState]);
  const handleMouseLeave = useCallback(() => setHoverState(false), [setHoverState]);

  const linkMaxWidth = (maxWidth * 2) / 3;
  if (linkMaxWidth < ICON_WIDTH_THRESHOLD_PX) {
    sourceLinkStyle = 'icon-only';
  }

  // Icon-only version
  if (sourceLinkStyle === 'icon-only') {
    return (
      <NavigateLink onClick={onClick} pinId={pinId} to={sourceUrl}>
        <IconButton
          accessibilityLabel={i18n._('link', 'Website url link')}
          bgColor="white"
          icon="arrow-up-right"
          iconColor="darkGray"
          iconSize={12}
          size={linkMaxWidth > ICON_SIZE_THRESHOLD_PX ? 'sm' : 'xs'}
        />
      </NavigateLink>
    );
  }

  return (
    <Box
      dangerouslySetInlineStyle={{
        __style: {
          ...styles.container,
          ...(isHovered ? styles.containerHovered : {}),
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      position="relative"
      maxWidth={linkMaxWidth}
    >
      <NavigateLink onClick={onClick} pinId={pinId} to={sourceUrl}>
        <Box
          alignItems="baseline"
          dangerouslySetInlineStyle={{ __style: styles.text }}
          display="flex"
          justifyContent="between"
        >
          <Box display="flex" flex="none" marginTop={3} marginBottom={3} marginStart={3}>
            <Icon
              accessibilityLabel={i18n._('link', 'Website url link')}
              color="darkGray"
              icon="arrow-up-right"
              size={9}
            />
          </Box>

          <Box
            alignItems="center"
            display="flex"
            height={18}
            marginStart={2}
            marginEnd={3}
            overflow="hidden"
          >
            <Text color="darkGray" size="sm" weight="semibold" truncate>
              {domain}
            </Text>
          </Box>
        </Box>
      </NavigateLink>
    </Box>
  );
}
